import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { User as UserIcon, Home, LayoutDashboard, Settings as SettingsIcon, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import { V2Shell, V2PageHeader } from '../components/admin/v2/V2Shell';
import { UPDATE_PROFILE_MUTATION } from '../services/authQueries';
import axiosInstance from '../services/axiosInstance';
import photoUrl from '../utils/photoUrl';

/* ============================================================
   Profilo utente amministrativo — V2 (redesign 2026).
   Sostituisce ProfilePage.tsx mantenendone tutto il contenuto
   (intestazione con avatar/ruolo/status, dati profilo, link
   rapidi) e aggiungendo la gestione della foto profilo
   (POST/DELETE /api/users/me/photo) e la modifica dei campi
   anagrafici via la mutation GraphQL `updateProfile`.
   ============================================================ */

const PHOTO_MAX_BYTES = 2 * 1024 * 1024;
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const STATUS_LABEL: Record<string, string> = {
  active: 'Associato Attivo',
  pending: 'In attesa',
  expired: 'Scaduto',
  suspended: 'Sospeso',
};

const cardStyle: React.CSSProperties = { padding: 20, marginBottom: 16 };

const ProfileV2: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const toast = useToast();
  const { confirm, ConfirmComponent } = useConfirm();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoButtonRef = useRef<HTMLButtonElement>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const [updateProfileMutation] = useMutation(UPDATE_PROFILE_MUTATION);

  useEffect(() => {
    setFirstName(user?.profile?.firstName || '');
    setLastName(user?.profile?.lastName || '');
    setPhoneNumber(user?.profile?.phoneNumber || '');
  }, [user]);

  // Parità con ProfilePage: stato "non autenticato" con invito al login.
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Accesso Richiesto</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Devi effettuare l'accesso per visualizzare il tuo profilo
          </p>
          <Link
            to="/admin/login"
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Accedi
          </Link>
        </div>
      </div>
    );
  }

  const displayName = user.profile?.fullName || user.username;
  const initials = displayName.trim().split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase() || 'A';
  const photo = photoUrl(user.profile?.foto) || user.profile?.profilePicture;
  const roleName = (user.roleId as any)?.name;
  const isAdminRole = roleName === 'amministratore' || roleName === 'admin';
  const statusKey = String(user.membershipStatus || '').toLowerCase();

  const handlePhotoSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    // L'input e nascosto: dopo la scelta il focus torna al bottone che l'ha aperto.
    photoButtonRef.current?.focus();
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      toast.error('Formato non valido', 'Sono ammessi solo file JPEG, PNG o WebP');
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      toast.error('File troppo grande', 'La foto non puo superare i 2 MB');
      return;
    }
    const fd = new FormData();
    fd.append('foto', file);
    setPhotoBusy(true);
    try {
      await axiosInstance.post('/users/me/photo', fd);
      await refreshUser();
      toast.success('Foto aggiornata');
    } catch (err: any) {
      toast.error('Errore caricamento foto', err?.response?.data?.message || 'Riprova piu tardi');
    } finally {
      setPhotoBusy(false);
    }
  };

  const handlePhotoRemove = async () => {
    const ok = await confirm({
      title: 'Rimuovi foto profilo',
      message: 'Vuoi rimuovere la tua foto profilo? Tornerai a essere mostrato con le iniziali.',
      type: 'danger',
      confirmText: 'Rimuovi',
      cancelText: 'Annulla',
    });
    if (!ok) return;
    setPhotoBusy(true);
    try {
      await axiosInstance.delete('/users/me/photo');
      await refreshUser();
      toast.success('Foto rimossa');
    } catch (err: any) {
      toast.error('Errore rimozione foto', err?.response?.data?.message || 'Riprova piu tardi');
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfileMutation({ variables: { input: { firstName, lastName, phoneNumber } } });
      await refreshUser();
      toast.success('Profilo aggiornato');
    } catch (err: any) {
      toast.error('Errore aggiornamento profilo', err?.message || 'Riprova piu tardi');
    } finally {
      setSaving(false);
    }
  };

  const quickLinks: { to: string; icon: React.ReactNode; title: string; desc: string }[] = [];
  if (isAdminRole) {
    quickLinks.push({ to: '/admin', icon: <LayoutDashboard size={18} />, title: 'Dashboard Admin', desc: 'Gestisci il sito' });
    quickLinks.push({ to: '/admin/settings', icon: <SettingsIcon size={18} />, title: 'Impostazioni', desc: 'Configura il sito' });
  }
  if (statusKey === 'active') {
    quickLinks.push({ to: '/members', icon: <Users size={18} />, title: 'Area Riservata', desc: 'Contenuti per associati' });
  }
  quickLinks.push({ to: '/', icon: <Home size={18} />, title: 'Home', desc: 'Torna alla home' });

  return (
    <V2Shell breadcrumb={[{ label: 'Dashboard', to: '/admin' }, { label: 'Profilo' }]}>
      <V2PageHeader icon={<UserIcon size={22} />} title="Il mio profilo" subtitle="Dati dell'account e foto profilo" />

      <div className="a2-wrap" style={{ paddingTop: 18, paddingBottom: 56 }}>
        {/* Intestazione: avatar + nome + ruolo + status */}
        <div className="a2-card" style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            {photo ? (
              <img
                src={photo}
                alt={`Foto di ${displayName}`}
                style={{ width: 96, height: 96, borderRadius: 999, objectFit: 'cover', flex: 'none', border: '1px solid var(--a2-bd2)' }}
              />
            ) : (
              <span
                data-testid="profile-initials"
                style={{ width: 96, height: 96, borderRadius: 999, background: 'var(--a2-green-800)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', font: "700 28px 'Sora', sans-serif", flex: 'none' }}
              >
                {initials}
              </span>
            )}

            <div style={{ flex: 1, minWidth: 220 }}>
              <h2 className="a2-h2" style={{ fontSize: 18 }}>{displayName}</h2>
              <p style={{ margin: '4px 0 0', font: "500 13px 'DM Sans', sans-serif", color: 'var(--a2-mut)' }}>{user.email}</p>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <span
                  className="a2-badge"
                  style={{
                    backgroundColor: (user.roleId as any)?.color ? `${(user.roleId as any).color}20` : undefined,
                    color: (user.roleId as any)?.color || undefined,
                  }}
                >
                  {(user.roleId as any)?.displayName || 'N/A'}
                </span>
                {user.membershipStatus && (
                  <span className={`a2-badge ${statusKey === 'active' ? 'a2-badge--green' : 'a2-badge--chip'}`}>
                    {STATUS_LABEL[statusKey] || user.membershipStatus}
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                ref={fileInputRef}
                id="profile-photo-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                aria-label="Scegli la nuova foto profilo (JPEG, PNG o WebP, massimo 2 MB)"
                style={{ display: 'none' }}
                data-testid="profile-photo-input"
                onChange={handlePhotoSelected}
              />
              <button
                ref={photoButtonRef}
                className="a2-btn a2-btn--primary"
                disabled={photoBusy}
                aria-controls="profile-photo-input"
                onClick={() => fileInputRef.current?.click()}
              >
                {photoBusy ? 'Attendere...' : 'Cambia foto'}
              </button>
              {user.profile?.foto && (
                <button className="a2-btn a2-btn--danger" disabled={photoBusy} onClick={handlePhotoRemove}>
                  Rimuovi foto
                </button>
              )}
              <span style={{ font: "400 11px 'DM Sans', sans-serif", color: 'var(--a2-fnt)', maxWidth: 170 }}>
                JPEG, PNG o WebP, massimo 2 MB.
              </span>
            </div>
          </div>
        </div>

        {/* Dati personali (modificabili) */}
        <form className="a2-card" style={cardStyle} onSubmit={handleSave}>
          <h3 className="a2-h2" style={{ marginBottom: 14 }}>Dati personali</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
            <label style={{ display: 'block' }}>
              <span className="a2-label">Nome</span>
              <input className="a2-input" style={{ marginTop: 6 }} value={firstName} onChange={e => setFirstName(e.target.value)} />
            </label>
            <label style={{ display: 'block' }}>
              <span className="a2-label">Cognome</span>
              <input className="a2-input" style={{ marginTop: 6 }} value={lastName} onChange={e => setLastName(e.target.value)} />
            </label>
            <label style={{ display: 'block' }}>
              <span className="a2-label">Telefono</span>
              <input className="a2-input" style={{ marginTop: 6 }} value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} />
            </label>
          </div>
          <div style={{ marginTop: 16 }}>
            <button className="a2-btn a2-btn--primary" type="submit" disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva modifiche'}
            </button>
          </div>
        </form>

        {/* Account (sola lettura) */}
        <div className="a2-card" style={cardStyle}>
          <h3 className="a2-h2" style={{ marginBottom: 14 }}>Account</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
            <div>
              <span className="a2-label">Username</span>
              <p style={{ margin: '4px 0 0', font: "500 13.5px 'DM Sans', sans-serif", color: 'var(--a2-tx)' }}>{user.username}</p>
            </div>
            <div>
              <span className="a2-label">Email</span>
              <p style={{ margin: '4px 0 0', font: "500 13.5px 'DM Sans', sans-serif", color: 'var(--a2-tx)' }}>{user.email}</p>
            </div>
            <div>
              <span className="a2-label">Nome completo</span>
              <p style={{ margin: '4px 0 0', font: "500 13.5px 'DM Sans', sans-serif", color: 'var(--a2-tx)' }}>{user.profile?.fullName || '—'}</p>
            </div>
            <div>
              <span className="a2-label">Telefono</span>
              <p style={{ margin: '4px 0 0', font: "500 13.5px 'DM Sans', sans-serif", color: 'var(--a2-tx)' }}>{user.profile?.phoneNumber || '—'}</p>
            </div>
            <div>
              <span className="a2-label">Ruolo</span>
              <p style={{ margin: '4px 0 0', font: "500 13.5px 'DM Sans', sans-serif", color: 'var(--a2-tx)' }}>{(user.roleId as any)?.displayName || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Link rapidi (parità con ProfilePage) */}
        <div className="a2-card" style={cardStyle}>
          <h3 className="a2-h2" style={{ marginBottom: 14 }}>Link rapidi</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            {quickLinks.map(l => (
              <Link
                key={l.to}
                to={l.to}
                className="a2-row"
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, border: '1px solid var(--a2-bd2)', borderRadius: 12, textDecoration: 'none' }}
              >
                <span style={{ color: 'var(--a2-green-500)', flex: 'none' }}>{l.icon}</span>
                <span>
                  <span style={{ display: 'block', font: "600 13px 'Sora', sans-serif", color: 'var(--a2-tx)' }}>{l.title}</span>
                  <span style={{ display: 'block', font: "400 12px 'DM Sans', sans-serif", color: 'var(--a2-mut)' }}>{l.desc}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <ConfirmComponent />
    </V2Shell>
  );
};

export default ProfileV2;
